import { useState, useCallback, useEffect, useMemo } from 'react';
import { IAddressValues, Parameter } from '../types/SetpointManagerTypes';
import {
  readControllerSetPoints,
  writeControllerSetPoints,
  backupSetPoints,
} from '../services/setpoints/SetpointService';
import { useParams } from 'react-router-dom';
import { Filters, SortingRule } from 'react-table';
import { useAppSelector, useAppDispatch } from './storeHooks';
import { useSelector } from 'react-redux';
import {
  fetchGroups,
  updateGroup,
  applySetpointPayload,
  setSelectedRows as setSelectedRowsAction,
  clearSelectedRows,
} from '../features/setpoints-manager/parameterGroupsSlice';
import type { RootState } from '../store';

interface UrlParams {
  assetId: string;
}

export const useParameterGroups = () => {
  const dispatch = useAppDispatch();
  const { assetId } = useParams<keyof UrlParams>() as UrlParams;
  const groups = useSelector((state: RootState) => state.parameterGroups.groups);
  const loading = useSelector((state: RootState) => state.parameterGroups.loading);
  const error = useSelector((state: RootState) => state.parameterGroups.error);
  const [globalSelectAll, setGlobalSelectAll] = useState(false);
  const selectedRowsArray = useSelector((state: RootState) => state.parameterGroups.selectedRows);
  const selectedRows = useMemo(() => new Set<string>(selectedRowsArray), [selectedRowsArray]);
  const [sortData, setSortData] = useState<Record<string, SortingRule<Parameter>[]>>({});
  const [filters, setFilters] = useState<Record<string, Filters<Parameter>>>({});
  const socketId = useAppSelector((state) => state.assetGroups?.socketId) ?? '';

  useEffect(() => {
    if (assetId) {
      dispatch(fetchGroups({ assetId }));
    }
  }, [assetId, dispatch]);

  const toggleGroupExpansion = useCallback(
    (groupId: string) => {
      const group = groups.find((g) => g.setpointGroup === groupId);
      if (group) {
        dispatch(updateGroup({ ...group, isExpanded: !group.isExpanded }));
      }
    },
    [groups, dispatch],
  );

  const toggleAll = useCallback(
    (expand: boolean) => {
      groups.forEach((group) => {
        dispatch(updateGroup({ ...group, isExpanded: expand }));
      });
    },
    [groups, dispatch],
  );

  const toggleGlobalSelectAll = (select: boolean) => {
    setGlobalSelectAll(select);
    if (select) {
      const selectRows = new Set<string>();
      groups.forEach((group) => {
        group.setpoints.forEach((param) => {
          selectRows.add(param.parameter);
        });
      });
      dispatch(setSelectedRowsAction(Array.from(selectRows)));
    } else {
      dispatch(clearSelectedRows());
    }
  };

  // Check if globalSelectAll should be unchecked when individual items are deselected
  useEffect(() => {
    if (globalSelectAll && groups.length > 0) {
      const allParameterIds = new Set<string>();
      groups.forEach((group) => {
        group.setpoints.forEach((param) => {
          allParameterIds.add(param.parameter);
        });
      });

      // If not all parameters are selected, uncheck globalSelectAll
      const allSelected = Array.from(allParameterIds).every((parameterId) => selectedRows.has(parameterId));

      if (!allSelected) {
        setGlobalSelectAll(false);
      }
    }
  }, [selectedRows, groups, globalSelectAll]);

  const updateSortData = (group: string, sortData: SortingRule<Parameter>[]) => {
    setSortData((prev) => ({ ...prev, [group]: sortData }));
  };
  const updateFilters = (group: string, filterData: Filters<Parameter>) => {
    setFilters((prev) => ({ ...prev, [group]: filterData }));
  };
  const readController = () => {
    const readStatus = readControllerSetPoints(assetId, Array.from(selectedRows), socketId);
    return readStatus;
  };
  const writeController = (addressValues: IAddressValues) => {
    return writeControllerSetPoints(assetId, addressValues, socketId);
  };

  const backupSetpoints = async (addressValues: IAddressValues) => {
    const res = await backupSetPoints(assetId, addressValues, socketId);
    // On success, update backupValue in store to reflect new backup state
    if (res) {
      try {
        dispatch(fetchGroups({ assetId, showLoading: false }));
      } catch {
        // no-op: UI already shows toast, and this is a local state sync
      }
    }
    return res;
  };
  const restoreSetpoints = async (addressValues: IAddressValues) => {
    return writeControllerSetPoints(assetId, addressValues, socketId);
  };
  // Update rows from payload via thunk to avoid stale state
  const updatedRows = useCallback(
    (payload: Record<number, number[]>) => {
      dispatch(applySetpointPayload(payload));
    },
    [dispatch],
  );

  return {
    groups,
    loading,
    error,
    toggleGroupExpansion,
    toggleAll,
    globalSelectAll,
    toggleGlobalSelectAll,
    selectedRows,
    updateSortData,
    sortData,
    filters,
    updateFilters,
    readController,
    updatedRows,
    writeController,
    backupSetpoints,
    restoreSetpoints,
  };
};
